import axios from "axios";
import sourceUrl from "../api/Source.api";
import { jwtDecode } from "jwt-decode";
import { refreshAuthToken } from "../contexts/AuthUtils";
import { InvokeMatrixApi } from "../contexts/WorkspaceUtils";

// 🔹 API Base URL
const API_BASE_URL = sourceUrl;

// 🔹 Base URL (Skip These Requests)
const SKIP_DOMAINS = [
  // "firebaseio.com", 
  // "firebaseapp.com", 
  // "googleapis.com", 
  "google-analytics.com", "hot-update.json", "/api/add",
  "testingprototype.s3.",
  "static.modalx.ai","microlink.io"
];

// 🔹 Get Auth State
const getAuthToken = () => localStorage.getItem("authToken");
const getTempToken = () => localStorage.getItem("tempToken");
const getUserId = () => localStorage.getItem("userId");
const isLoggedIn = () => localStorage.getItem("isLoggedIn") === "true";

// ✅ Function to Validate Token
const isTokenValid = (token) => {
  if (!token) return false;
  try {
    const decoded = jwtDecode(token);
    return decoded.exp * 1000 > Date.now();
  } catch (error) {
    console.error("Invalid token:", error);
    return false;
  }
};

// ✅ Function to Get a Valid Token (Refresh If Needed)
const getValidAuthToken = async () => {
  if (!isLoggedIn()) return null;
  
  let authToken = getAuthToken();
  if (!isTokenValid(authToken)) {
    try {
      authToken = await refreshAuthToken();
    } catch (error) {
      console.error("Failed to refresh token:", error);
      throw error;
    }
  }
  return authToken;
};

// ✅ Check if Request Should Skip Auth
const shouldSkipAuth = (url) => {
  if (!url) return true;
  return SKIP_DOMAINS.some(domain => url.includes(domain));
};

// ✅ Ensure Fetch is Available
if (!window.fetch || typeof window.fetch !== "function") {
  console.warn("⚠️ window.fetch is missing, restoring original fetch!");
  window.fetch = window.__originalFetch || fetch;
}

// ✅ Store Original Fetch Safely
window.__originalFetch = window.__originalFetch || window.fetch;

// ✅ Override Fetch (Track API Calls)
window.fetch = async (url, options = {}) => {
  try {
    if (!window.__originalFetch) {
      throw new Error("window.fetch is unavailable.");
    }

    // Skip requests that don't need auth
    if (shouldSkipAuth(url)) {
      return window.__originalFetch(url, options);
    }

    // Only proceed with token logic if logged in
    const authToken = isLoggedIn() ? await getValidAuthToken() : null;
    const tempToken = getTempToken();
    const userId = getUserId();

    const headers = {
      // "Content-Type": "application/json",
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      // ...(authToken ? { Authorization: `Bearer eyJhbGciOiJSUzI1NiIsImtpZCI6IjkwOTg1NzhjNDg4MWRjMDVlYmYxOWExNWJhMjJkOGZkMWFiMzRjOGEiLCJ0eXAiOiJKV1QifQ.eyJuYW1lIjoiUHJhdGlrc2hhIENob3VkaGFyeSIsInBpY3R1cmUiOiJodHRwczovL2xoMy5nb29nbGV1c2VyY29udGVudC5jb20vYS9BQ2c4b2NKcHNDTlNMQ0luaWtjR0hBemZSZ3VvOGRucmhkVFU2WGhOSzBrT1dIcVpiQ1dLMHc9czk2LWMiLCJpc3MiOiJodHRwczovL3NlY3VyZXRva2VuLmdvb2dsZS5jb20vbW9kYWx4LWRldi1jMDg2NiIsImF1ZCI6Im1vZGFseC1kZXYtYzA4NjYiLCJhdXRoX3RpbWUiOjE3NDIxMjc3ODgsInVzZXJfaWQiOiJTZ0t2emEyenJpZ2lkZGsxTVlxNEIydnUzTmwxIiwic3ViIjoiU2dLdnphMnpyaWdpZGRrMU1ZcTRCMnZ1M05sMSIsImlhdCI6MTc0NTQwOTIyMSwiZXhwIjoxNzQ1NDEyODIxLCJlbWFpbCI6InByYXRpa3NoYUBtb2RhbHguYWkiLCJlbWFpbF92ZXJpZmllZCI6dHJ1ZSwiZmlyZWJhc2UiOnsiaWRlbnRpdGllcyI6eyJnb29nbGUuY29tIjpbIjExNTI3Njc5MjU4MDE0MTUwNDI5NiJdLCJlbWFpbCI6WyJwcmF0aWtzaGFAbW9kYWx4LmFpIl19LCJzaWduX2luX3Byb3ZpZGVyIjoiZ29vZ2xlLmNvbSJ9fQ.Kz1_PnjwU-Kmaw-OBBTHqwS2DhOID-gEKPSiaXccgIuOX_aod6KiGByPVF_TavLI_fYDJmTadWRZO5D6SpA3-A7EE5tqw_P8v-GsCvPU7S47QOuLmcAdNfqtgFK07XhqdrF1m9ZsQHNVOFcOlUMfGRE_C8UVvj3dEwLN1bxZAVUnhDYdYr_xGBKcwP1uhmuEzTUUq9ejyPtA5eNGz9jAtJpT0cKq-RtdGJG5I23vmRpQtO0aXi4CG3HZbx68PXxEqedo3bHsOG-e73O4vBenrjE76OOQHrTWYSsNFdm6X9Mk_6TaNE6QxrNX6UdpUTUVZM9hxn5d9O2PbK6SGta4WA` } : {}),
      ...(tempToken ? { "X-Admin-Token": `{Bearer ${tempToken}}` } : {}),
      ...options.headers,
    };

    const response = await window.__originalFetch(
      url.startsWith("http") ? url : `${API_BASE_URL}${url}`,
      { ...options, headers }
    );

    // Track API Response if logged in
    // if (userId && isLoggedIn() && !url.includes("/add")) {
    // }
    const clonedResponse = response.clone();
    const jsonData = await clonedResponse.json();
    await InvokeMatrixApi( (url.includes("firebaseapp.com") || url.includes("googleapis.com") || url.includes("firebaseio.com")) ? jsonData : {}, response, options?.method, null, url, options?.body || {}, userId);

    return response;
  } catch (error) {
    const userId = getUserId();
    console.error("Fetch error:", error);

    if (userId && isLoggedIn() && !url.includes("/add")) {
      await InvokeMatrixApi(error, error, options?.method, null, url, options?.body, userId);
    }

    throw error;
  }
};

// ✅ Configure Axios Globally
axios.defaults.baseURL = API_BASE_URL;
// axios.defaults.headers.common["Content-Type"] = "application/json";

// 🔹 Axios Request Interceptor
axios.interceptors.request.use(async (config) => {
  if (shouldSkipAuth(config.url)) {
    return config;
  }

  // Only proceed with token logic if logged in
  if (isLoggedIn()) {
    try {
      const authToken = await getValidAuthToken();
      const tempToken = getTempToken();

      if (authToken) {
        config.headers.Authorization = `Bearer ${authToken}`;
        // config.headers.Authorization = `Bearer eyJhbGciOiJSUzI1NiIsImtpZCI6IjkwOTg1NzhjNDg4MWRjMDVlYmYxOWExNWJhMjJkOGZkMWFiMzRjOGEiLCJ0eXAiOiJKV1QifQ.eyJuYW1lIjoiUHJhdGlrc2hhIENob3VkaGFyeSIsInBpY3R1cmUiOiJodHRwczovL2xoMy5nb29nbGV1c2VyY29udGVudC5jb20vYS9BQ2c4b2NKcHNDTlNMQ0luaWtjR0hBemZSZ3VvOGRucmhkVFU2WGhOSzBrT1dIcVpiQ1dLMHc9czk2LWMiLCJpc3MiOiJodHRwczovL3NlY3VyZXRva2VuLmdvb2dsZS5jb20vbW9kYWx4LWRldi1jMDg2NiIsImF1ZCI6Im1vZGFseC1kZXYtYzA4NjYiLCJhdXRoX3RpbWUiOjE3NDIxMjc3ODgsInVzZXJfaWQiOiJTZ0t2emEyenJpZ2lkZGsxTVlxNEIydnUzTmwxIiwic3ViIjoiU2dLdnphMnpyaWdpZGRrMU1ZcTRCMnZ1M05sMSIsImlhdCI6MTc0NTQxMjc2MSwiZXhwIjoxNzQ1NDE2MzYxLCJlbWFpbCI6InByYXRpa3NoYUBtb2RhbHguYWkiLCJlbWFpbF92ZXJpZmllZCI6dHJ1ZSwiZmlyZWJhc2UiOnsiaWRlbnRpdGllcyI6eyJnb29nbGUuY29tIjpbIjExNTI3Njc5MjU4MDE0MTUwNDI5NiJdLCJlbWFpbCI6WyJwcmF0aWtzaGFAbW9kYWx4LmFpIl19LCJzaWduX2luX3Byb3ZpZGVyIjoiZ29vZ2xlLmNvbSJ9fQ.H8k1svO1JhneNC5QssKWN8EWCPUdmvDjZieY9BEAK0a5xt1sj1hVamv-_retm9SvliLdvlb3KTpa83_fKAXjCkmLII-VX8ZPuz_mD156BBrvcJTNnM45s35AUV7fzhrYjSEWSvzZucMlSDUgPLeq2ClOvVBVyv_JFzCMzwfsMX2ekx0NyTJ8jdYCAF8VRaJgwWCyTs_l--wVVOrbyuu63XYWXYXX4g7limDYdP2u7dqxPGqEbg7dc_gs_nufYVVDl7i7rZIzfO_HfKaqWcJdFyow0ZWMizLZ67aW8MqBRcamfupYWBd1gV8bpagUnJrvrFF-MfvEdDP0GTfIvdVc_g`;
      }
      if (tempToken) {
        config.headers["X-Admin-Token"] = `{Bearer ${tempToken}}`;
      }
    } catch (error) {
      console.error("Token validation failed:", error);
      throw error;
    }
  }

  return config;
});

// 🔹 Axios Response Interceptor
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.error("Axios error:", error);

    const userId = getUserId();
    if (userId && isLoggedIn()) {
      await InvokeMatrixApi(error, error.response, error.config?.method, null, error.config?.url, error.config?.data, userId);
    }

    return Promise.reject(error);
  }
);


// import axios from "axios";
// import sourceUrl from "../api/Source.api";
// import { jwtDecode } from "jwt-decode";
// import { refreshAuthToken } from "../contexts/AuthUtils";
// import { InvokeMatrixApi } from "../contexts/WorkspaceUtils";

// // 🔹 Your API Base URL
// const API_BASE_URL = sourceUrl;

// // 🔹 Firebase Base URL (Skip These Requests)
// const FIREBASE_DOMAINS = ["firebaseio.com", "firebaseapp.com", "googleapis.com", "google-analytics.com"];

// // 🔹 Get Tokens from Local Storage
// const getAuthToken = () => localStorage.getItem("authToken");
// const getTempToken = () => localStorage.getItem("tempToken");
// const getUserId = () => localStorage.getItem("userId");

// // ✅ Function to Validate Token
// const isTokenValid = (token) => {
//   if (!token) return false;
//   try {
//     const decoded = jwtDecode(token);
//     return decoded.exp * 1000 > Date.now(); // Check expiration
//   } catch (error) {
//     console.error("Invalid token:", error);
//     return false;
//   }
// };

// // ✅ Function to Get a Valid Token (Refresh If Needed)
// const getValidAuthToken = async () => {
//   let authToken = getAuthToken();
//   if (!isTokenValid(authToken)) {
//     try {
//       authToken = await refreshAuthToken();
//     } catch (error) {
//       console.error("Failed to refresh token:", error);
//       throw error; // Handle failed refresh (e.g., force logout)
//     }
//   }
//   return authToken;
// };


// // ✅ Override Fetch Globally (Skip Firebase Calls)
// const originalFetch = window.fetch;
// window.fetch = async (url, options = {}) => {
//   if (FIREBASE_DOMAINS.some((domain) => url.includes(domain))) {
//     return originalFetch(url, options);
//   }

//   try {
//     const authToken = await getValidAuthToken();
//     const tempToken = getTempToken();
//     const userId = getUserId();

//     const headers = {
//       "Content-Type": "application/json",
//       ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
//       ...(tempToken ? { "X-Admin-Token": `{Bearer ${tempToken}}` } : {}),
//       ...options.headers,
//     };

//     const response = await originalFetch(
//       url.startsWith("http") ? url : `${API_BASE_URL}${url}`,
//       { ...options, headers }
//     );

//      const clonedResponse = response.clone();
//      const jsonData = await clonedResponse.json(); 
 
//      // Call InvokeMatrixApi with JSON data without affecting the original response
//      if(userId &&  !url.includes('/add')){
//        await InvokeMatrixApi(jsonData, response, options?.method, null, url, options?.body ? options?.body : {}, userId);
//      }
 
//     return response;
//   } catch (error) {
//     const userId = getUserId();
//     console.error("Fetch error:", error);
//     // console.log("response error", error);
//     if( userId && !url.includes('/add')){
//       await InvokeMatrixApi(error, error, options?.method, null, url, options?.body, userId);
//     }
//     throw error;
//   }
// };

// // ✅ Configure Axios Globally (Skip Firebase Calls)
// axios.defaults.baseURL = API_BASE_URL;
// axios.defaults.headers.common["Content-Type"] = "application/json";

// // 🔹 Axios Request Interceptor (Checks Token Before Each Request)
// axios.interceptors.request.use(async (config) => {
//   if (FIREBASE_DOMAINS.some((domain) => config.url.includes(domain))) {
//     return config;
//   }

//   try {
//     const authToken = await getValidAuthToken();
//     const tempToken = getTempToken();

//     if (authToken) {
//       config.headers.Authorization = `Bearer ${authToken}`;
//     }
//     if (tempToken) {
//       config.headers["X-Admin-Token"] = `{Bearer ${tempToken}}`;
//     }
//   } catch (error) {
//     console.error("Token validation failed:", error);
//     throw error;
//   }

//   return config;
// });

// // 🔹 Axios Response Interceptor
// axios.interceptors.response.use(
//   (response) => response,
//   (error) => {
//     console.error("Axios error:", error);
//     return Promise.reject(error);
//   }
// );